import { ChevronDown, Link2, Plus } from "lucide-react";
import { useCanEdit } from "@/model/access";
import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { LinkDialog } from "@/components/links/LinkDialog";
import { LinkRow } from "@/components/links/LinkRow";
import { useForecastHistory } from "@/hooks/world";
import { byClient, clientPrefix } from "@/lib/format";
import { cn } from "@/lib/utils";
import { dependants, drivers } from "@/model/graph";
import { useStore } from "@/model/store";
import { linkFrom, type ModelNode, type Link as LinkT } from "@/model/types";
import { NewNodeDialog } from "./NewNodeDialog";
import { NodeIcon } from "@/components/NodeIcon";

type Kind = "event" | "metric";

/** A side-panel section. Headings are h2 so the page reads h1 → h2 (Lighthouse heading order). */
export function Section({
	title,
	children,
	action,
	quiet,
}: {
	title: string;
	children: React.ReactNode;
	action?: React.ReactNode;
	/** Admin content: a quieter heading. */
	quiet?: boolean;
}) {
	return (
		<section className="border-t pt-6 pb-5 first:border-t-0 first:pt-0">
			<div className="mb-3 flex min-h-8 items-center justify-between gap-2">
				<h2 className={cn("text-xs font-semibold tracking-[0.08em] uppercase", quiet ? "text-muted-foreground" : "text-foreground")}>{title}</h2>
				{action}
			</div>
			{children}
		</section>
	);
}

/** A quieter group at the bottom of the side column: resolution and forecast history. */
export function AdminGroup({ children }: { children: React.ReactNode }) {
	return (
		<div className="mt-2 border-t pt-5">
			<p className="mb-4 text-[11px] font-medium tracking-[0.08em] text-muted-foreground/80 uppercase">Admin</p>
			<div className="[&>section]:border-t-0 [&>section]:pt-0">{children}</div>
		</div>
	);
}

/**
 * The "Add driver" / "Add link" menus and the dialogs they open. Shared by the side panel and the
 * "Next: add what drives it" callout on a node with no links.
 */
function useLinkCreation(node: ModelNode, scope?: string[]) {
	const [dialog, setDialog] = useState<{ from?: string; to?: string } | null>(null);
	const [creating, setCreating] = useState<{ dir: "driver" | "affects"; kind: Kind } | null>(null);
	const [tuning, setTuning] = useState<LinkT | null>(null);
	const canEdit = useCanEdit();

	// Drivers of a metric can only be events (metrics feed metrics through formulas).
	const driverKinds: Kind[] = node.kind === "metric" ? ["event"] : ["event", "metric"];
	const affectKinds: Kind[] = node.kind === "metric" ? ["event"] : ["event", "metric"];
	const here = node.pos;
	const newPos = creating && here ? { x: here.x + (creating.dir === "driver" ? -300 : 300), y: here.y + 40 } : undefined;

	/** "Add driver" / "Add link" made the node and a default link in one step; tune the link next. */
	function linked(_id: string, link?: LinkT) {
		setCreating(null);
		if (link) setTuning(link);
	}

	// Viewers read links but can't add them.
	const menu = (dir: "driver" | "affects", label: string, variant: "ghost" | "outline" = "ghost") =>
		canEdit && (
			<DropdownMenu>
				<DropdownMenuTrigger asChild>
					<Button variant={variant} size="sm">
						<Plus className="size-4" /> {label} <ChevronDown className="size-3.5 opacity-60" />
					</Button>
				</DropdownMenuTrigger>
				<DropdownMenuContent align="end">
					{(dir === "driver" ? driverKinds : affectKinds).map((k) => (
						<DropdownMenuItem key={k} onSelect={() => setCreating({ dir, kind: k })}>
							<Plus className="size-4" /> New {k}
						</DropdownMenuItem>
					))}
					<DropdownMenuSeparator />
					<DropdownMenuItem onSelect={() => setDialog(dir === "driver" ? { to: node.id } : { from: node.id })}>
						<Link2 className="size-4" /> Link an existing one…
					</DropdownMenuItem>
				</DropdownMenuContent>
			</DropdownMenu>
		);

	const dialogs = (
		<>
			<LinkDialog open={!!dialog} onOpenChange={(v) => !v && setDialog(null)} from={dialog?.from} to={dialog?.to} scope={scope} />
			<NewNodeDialog
				open={!!creating}
				onOpenChange={(v) => !v && setCreating(null)}
				kind={creating?.kind ?? "event"}
				kinds={creating ? (creating.dir === "driver" ? driverKinds : affectKinds) : ["event"]}
				heading={creating ? (creating.dir === "driver" ? `New driver of ${node.title}` : `New node affected by ${node.title}`) : undefined}
				pos={newPos}
				connect={creating ? { to: node.id, dir: creating.dir === "driver" ? "driver" : "dependent" } : undefined}
				onCreated={linked}
			/>
			{/* The new link is already in place; this sets its numbers straight away. */}
			<LinkDialog open={!!tuning} onOpenChange={(v) => !v && setTuning(null)} edit={tuning ?? undefined} />
		</>
	);
	return { menu, dialogs };
}

/** True when a node stands alone: no links either way, no formula or definition, used in nothing. */
export function useIsolated(node: ModelNode): boolean {
	const links = useStore((s) => s.world.links);
	const nodes = useStore((s) => s.world.nodes);
	if (node.kind === "event" && node.definition) return false;
	if (node.kind === "metric" && node.formula) return false;
	if (links.some((l) => l.to === node.id || linkFrom(l) === node.id)) return false;
	return dependants(node.id, nodes, links).length === 0;
}

/** Under the hero on a node with no links: the next step, with the same two menus as the side panel. */
export function NextStepCallout({ node }: { node: ModelNode }) {
	const { menu, dialogs } = useLinkCreation(node);
	return (
		<div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border border-dashed border-foreground/25 px-4 py-3">
			<p className="text-sm">
				<span className="font-semibold">Next:</span> add what drives it, or what it affects.
			</p>
			<div className="ml-auto flex flex-wrap gap-2">
				{menu("driver", "Add driver", "outline")}
				{menu("affects", "Add link", "outline")}
			</div>
			{dialogs}
		</div>
	);
}

export function LinksPanel({ node, scope }: { node: ModelNode; scope?: string[] }) {
	const links = useStore((s) => s.world.links);
	const nodes = useStore((s) => s.world.nodes);
	const removeLink = useStore((s) => s.removeLink);
	const analyst = useStore((s) => s.analyst);
	const { menu, dialogs } = useLinkCreation(node, scope);

	const incoming = links.filter((l) => l.to === node.id);
	const outgoing = links.filter((l) => linkFrom(l) === node.id);
	const usedIn = dependants(node.id, nodes, links).filter((id) => !links.some((l) => l.to === id && linkFrom(l) === node.id));
	const canHaveIncoming = !(node.kind === "event" && node.definition) && !(node.kind === "metric" && node.formula);
	const formulaInputs = canHaveIncoming ? [] : drivers(node.id, nodes, links);

	return (
		<>
			<Section
				title={canHaveIncoming ? "Drivers" : node.kind === "metric" ? "Its formula uses" : "Its definition uses"}
				action={canHaveIncoming && menu("driver", "Add driver")}
			>
				{!canHaveIncoming ? (
					// Calculated nodes: the drivers come from the formula or definition, parsed, not from links.
					<div className="text-sm">
						{formulaInputs.length ? (
							<ul className="space-y-1">
								{formulaInputs.map((id) => (
									<li key={id}>
										<Link className="hover:underline" to={`/node/${id}`}>
											<NodeIcon node={nodes[id]} className="mr-1.5 text-muted-foreground" />
											{nodes[id].title}
										</Link>
										{analyst && <code className="ml-2 font-mono text-[11px] text-muted-foreground">{id}</code>}
									</li>
								))}
							</ul>
						) : (
							<p className="text-muted-foreground">Nothing yet.</p>
						)}
						<p className="mt-2 text-xs text-muted-foreground">
							{node.kind === "metric"
								? "Edit the formula to change these; each name in it becomes a driver automatically."
								: "Edit the definition to change these."}
						</p>
					</div>
				) : incoming.length ? (
					<div className="divide-y">
						{incoming.map((l) => (
							<LinkRow key={l.id} link={l} onRemove={() => removeLink(l.id)} />
						))}
					</div>
				) : (
					<p className="text-sm text-muted-foreground">
						{node.kind === "event"
							? "Nothing drives this yet. Add an event that could cause it, or a condition that makes it more likely."
							: "No events affect this yet. Add one to see the baseline “with risks”."}
					</p>
				)}
			</Section>
			<Section title="Used in" action={menu("affects", "Add link")}>
				{outgoing.length ? (
					<div className="divide-y">
						{outgoing.map((l) => (
							<LinkRow key={l.id} link={l} onRemove={() => removeLink(l.id)} />
						))}
					</div>
				) : usedIn.length === 0 ? (
					<p className="text-sm text-muted-foreground">Nothing yet.</p>
				) : null}
				{usedIn.length > 0 && (
					<p className="mt-2 text-sm text-muted-foreground">
						Used in{" "}
						{usedIn.map((id, i) => (
							<span key={id}>
								{i > 0 && ", "}
								<Link className="text-foreground hover:underline" to={`/node/${id}`}>
									<NodeIcon node={nodes[id]} className="mr-1 text-muted-foreground" />
									{nodes[id].title}
								</Link>
							</span>
						))}
					</p>
				)}
			</Section>
			{dialogs}
		</>
	);
}

export function HistoryPanel({ node }: { node: ModelNode }) {
	const { data } = useForecastHistory(node.id);
	const versions = data?.versions ?? [];
	if (!versions.length) return null;
	return (
		<Section title="Forecast history" quiet>
			<ol className="space-y-1.5 text-sm">
				{versions.slice(0, 8).map((h, i) => (
					<li key={h.version} className="flex gap-3">
						<time className="w-20 shrink-0 text-muted-foreground tabular-nums" dateTime={h.created_at}>
							{new Date(h.created_at).toLocaleDateString(undefined, { day: "numeric", month: "short" })}
						</time>
						<span className={i === 0 ? "" : "text-muted-foreground"}>
							{byClient(h.origin) && <span className="mr-1 font-medium">{clientPrefix(h.origin, h.client_name).trim()}</span>}
							{h.summary}
						</span>
					</li>
				))}
			</ol>
		</Section>
	);
}
